<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>
  <p-toast position="top-center"></p-toast>

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'MachineAssignment.title' | translateWF: 'Machine Assignment Import' }}</h1>
        <div class="page__title_buttons"></div>
      </div>
    </div>

    <p-panel [header]="'MachineAssignment.subtitle' | translateWF: 'Bulk Assign Machines'">
      <ng-template pTemplate="icons">
        <p-button
          [label]="'MachineAssignment.submit' | translateWF: 'Import'"
          icon="pi pi-upload"
          (onClick)="onSubmit()"
          [outlined]="true"
          [disabled]="form.invalid || isLoading() > 0"
        />
      </ng-template>

      <p-card>
        <div class="col">
          <p-message severity="secondary">
            {{ 'MachineAssignment.description' | translateWF: 'Select area, day and shift, then upload an .xlsx file.' }}
          </p-message>
          <form [formGroup]="form">
            <div class="col">
              <div class="grid-auto-4">
                <!-- Area -->
                <div class="form-box">
                  <label for="area">{{ 'MachineAssignment.area_label' | translateWF: 'Area' }} *</label>
                  <p-select
                    id="area"
                    class="w-full"
                    formControlName="area"
                    [options]="areaOptions"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'MachineAssignment.select_area' | translateWF: 'Select an area'"
                    appendTo="body"
                    fluid
                    [ngClass]="{ 'ng-invalid ng-dirty': area.invalid && area.touched }"
                  />
                  <!-- <small class="p-error" *ngIf="area.invalid && area.touched">
                    {{ 'MachineAssignment.area_required' | translateWF: 'Area is required' }}
                  </small> -->
                </div>

                <!-- Day -->
                <div class="form-box">
                  <label for="date">{{ 'MachineAssignment.date_label' | translateWF: 'Day' }} *</label>
                  <p-datepicker
                    id="date"
                    class="w-full"
                    formControlName="date"
                    [iconDisplay]="'input'"
                    [showIcon]="true"
                    dateFormat="yy-mm-dd"
                    appendTo="body"
                    fluid
                    [ngClass]="{ 'ng-invalid ng-dirty': date.invalid && date.touched }"
                  />
                  <small class="p-error" *ngIf="date.invalid && date.touched">
                    {{ 'MachineAssignment.date_required' | translateWF: 'Day is required' }}
                  </small>
                </div>

                <!-- Shift -->
                <div class="form-box">
                  <label for="shift">{{ 'MachineAssignment.shift_label' | translateWF: 'Shift' }} *</label>
                  <p-select
                    id="shift"
                    class="w-full"
                    formControlName="shift"
                    [options]="shiftOptions"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'MachineAssignment.select_shift' | translateWF: 'Select a shift'"
                    appendTo="body"
                    fluid
                    [ngClass]="{ 'ng-invalid ng-dirty': shift.invalid && shift.touched }"
                  />
                  <small class="p-error" *ngIf="shift.invalid && shift.touched">
                    {{ 'MachineAssignment.shift_required' | translateWF: 'Shift is required' }}
                  </small>
                </div>
              </div>
              <div class="grid-auto-2">
                <!-- File upload -->
                <div class="form-box">
                  <label for="fileInput">{{ 'MachineAssignment.file_label' | translateWF: 'Excel file (.xlsx)' }} *</label>
                  <input
                    #fileInput
                    id="fileInput"
                    class="w-full"
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    [disabled]="!canUploadFile"
                    (change)="onFileSelected($event)"
                    class="file-input"
                  />
                  <small class="file-hint" *ngIf="!canUploadFile">
                    {{ 'MachineAssignment.file_disabled_hint' | translateWF: 'Select area, day and shift first.' }}
                  </small>
                  <small class="p-error" *ngIf="file.invalid && file.touched && canUploadFile">
                    {{ 'MachineAssignment.file_required' | translateWF: 'File is required' }}
                  </small>
                </div>
              </div>
            </div>
          </form>
        </div>
      </p-card>

      <ng-template pTemplate="footer">
        <a class="sample-link" (click)="downloadTemplate()">
          <i class="pi pi-download"></i>
          {{ 'MachineAssignment.download_sample' | translateWF: 'Download Sample Template' }}
        </a>
      </ng-template>
    </p-panel>

    <p-panel [header]="'MachineAssignment.last_records_title' | translateWF: 'Last Imported Records'">
      <div class="col-xs">
        <!-- Filter panel -->
        <p-panel header="Filter" data-tone="surface">
          <ng-template pTemplate="icons">
            <p-button
              [label]="'MachineAssignment.filter_reset' | translateWF: 'Reset'"
              variant="outlined"
              severity="secondary"
              icon="pi pi-times"
              size="small"
              (onClick)="onResetFilter()"
            />
            <p-button
              [label]="'MachineAssignment.filter_apply' | translateWF: 'Apply Filter'"
              icon="pi pi-search"
              size="small"
              [disabled]="filterMode() === 'dateShift' ? !canApplyDateShiftFilter : !canApplyCostCenterFilter"
              (onClick)="onApplyFilter()"
            />
          </ng-template>
          <p-card data-variant="flat">
            <div class="col">
              <!-- Mode toggle -->
              <div class="row-sm">
                <p-button
                  [label]="'MachineAssignment.filter_mode_date_shift' | translateWF: 'By Date & Shift'"
                  [outlined]="filterMode() !== 'dateShift'"
                  size="small"
                  (onClick)="filterMode.set('dateShift')"
                />
                <p-button
                  [label]="'MachineAssignment.filter_mode_cost_center_month' | translateWF: 'By Cost Center & Month'"
                  [outlined]="filterMode() !== 'costCenterMonth'"
                  size="small"
                  (onClick)="filterMode.set('costCenterMonth')"
                />
              </div>

              <!-- Date + Shift filters -->
              @if (filterMode() === 'dateShift') {
                <div class="grid-auto-4">
                  <div class="form-box">
                    <label>{{ 'MachineAssignment.filter_date_label' | translateWF: 'Date' }}</label>
                    <p-datepicker class="w-full" [formControl]="filterDate" [iconDisplay]="'input'" [showIcon]="true" dateFormat="yy-mm-dd" appendTo="body" fluid />
                  </div>
                  <div class="form-box">
                    <label>{{ 'MachineAssignment.filter_shift_label' | translateWF: 'Shift' }}</label>
                    <p-select
                      class="w-full"
                      [formControl]="filterShift"
                      [options]="shiftOptions"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'MachineAssignment.select_shift' | translateWF: 'Select a shift'"
                      appendTo="body"
                      fluid
                    />
                  </div>
                </div>
              }

              <!-- Cost Center + Year + Month filters -->
              @if (filterMode() === 'costCenterMonth') {
                <div class="grid-auto-4">
                  <div class="form-box">
                    <label>{{ 'MachineAssignment.filter_cost_center_label' | translateWF: 'Cost Center' }}</label>
                    <p-select
                      class="w-full"
                      [formControl]="filterCostCenter"
                      [options]="areaOptions"
                      optionLabel="label"
                      optionValue="costCenterCode"
                      [placeholder]="'MachineAssignment.select_area' | translateWF: 'Select an area'"
                      appendTo="body"
                      fluid
                    />
                  </div>
                  <div class="form-box">
                    <label>{{ 'MachineAssignment.filter_year_label' | translateWF: 'Year' }}</label>
                    <p-select
                      class="w-full"
                      [formControl]="filterYear"
                      [options]="yearOptions"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'MachineAssignment.filter_year_label' | translateWF: 'Year'"
                      appendTo="body"
                      fluid
                    />
                  </div>
                  <div class="form-box">
                    <label>{{ 'MachineAssignment.filter_month_label' | translateWF: 'Month' }}</label>
                    <p-select
                      class="w-full"
                      [formControl]="filterMonth"
                      [options]="monthOptions"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'MachineAssignment.filter_month_label' | translateWF: 'Month'"
                      appendTo="body"
                      fluid
                    />
                  </div>
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        @if (lastRecords().length > 0) {
          <p-card data-variant="compact">
            <p-table [value]="lastRecords()" [paginator]="true" [rows]="10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'MachineAssignment.col_date' | translateWF: 'Date' }}</th>
                  <th>{{ 'MachineAssignment.col_asset' | translateWF: 'Asset' }}</th>
                  <th>{{ 'MachineAssignment.col_shift' | translateWF: 'Shift' }}</th>
                  <th>{{ 'MachineAssignment.col_employees' | translateWF: 'Employee Codes' }}</th>
                  <th>{{ 'MachineAssignment.col_supervisor' | translateWF: 'Supervisor' }}</th>
                  <th>{{ 'MachineAssignment.col_group' | translateWF: 'Group' }}</th>
                  <th>{{ 'MachineAssignment.col_cost_center' | translateWF: 'Cost Center' }}</th>
                  <th>{{ 'MachineAssignment.col_created_by' | translateWF: 'Created By' }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.date.year }}-{{ row.date.month | number: '2.0' }}-{{ row.date.day | number: '2.0' }}</td>
                  <td>{{ row.assetId }}</td>
                  <td>
                    {{ row.shift === 0 ? ('MachineAssignment.shift_Matutino' | translateWF: 'Matutino') : ('MachineAssignment.shift_Nocturno' | translateWF: 'Nocturno') }}
                  </td>
                  <td>{{ row.employeeCodes.join(', ') }}</td>
                  <td>{{ row.supervisor }}</td>
                  <td>{{ getGroupName(row.groupReference) }}</td>
                  <td>{{ row.costCenterCode ?? '-' }}</td>
                  <td>{{ row.createdBy }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        }
      </div>
    </p-panel>
  </div>
</main>
